/**
 * Field Creation Service
 * 
 * This file contains all field creation logic extracted from SolutionImportModal.
 * It handles creating ContactFields, ContactSets, ReportingTemplates, Topics, and ApiConnections.
 */

import { shortUUID } from "../../helpers";
import {
  createEmptyFieldUpdates,
  generateImportedLabel,
  getReportingTemplateFieldMappingUpdates,
} from "../utils/solutionImportHelpers";

const getCreatedUuid = (res: any): string =>
  res?.data?.uuid ||
  res?.data?.Uuid ||
  res?.data?.data?.uuid ||
  res?.data?.data?.Uuid ||
  res?.data?.template?.uuid ||
  res?.data?.template?.Uuid ||
  "";

/**
 * Create contact fields that need to be created
 */
export const createContactFields = async (
  importedJSON: any,
  manualFields: string[],
  upsertField: (data: any) => Promise<any>,
  updates: Record<string, Record<string, string>>
): Promise<void> => {
  const contactFieldPromises = Object.entries(
    importedJSON.Bag.External.ContactField || {}
  ).map(async ([fieldKey, fieldValue]: [string, any]) => {
    // Create field if: has NEW: prefix (manual with name) OR empty and not manual (auto-create)
    const shouldCreate =
      (fieldValue && fieldValue.startsWith("NEW:")) ||
      (!fieldValue && !manualFields.includes(fieldKey));

    if (shouldCreate) {
      const oldField = importedJSON.Item.ContactFields.find(
        (f: any) => f.uuid === fieldKey
      );
      if (!oldField) {
        console.error("Could not find old field definition for", fieldKey);
        return;
      }
      const newField = {
        ...oldField,
      };
      delete newField.order;
      newField.uuid = shortUUID();
      // If the value starts with "NEW:", use that label instead
      if (fieldValue && fieldValue.startsWith("NEW:")) {
        newField.label = fieldValue.substring(4); // Remove "NEW:" prefix
      }
      newField.options =
        oldField.options?.map((opt: any) => ({
          ...opt,
          uuid: shortUUID(),
        })) || [];
      const res: any = await upsertField({
        fields: [newField],
      });

      const createdUuid = getCreatedUuid(res) || newField.uuid;
      if (createdUuid) {
        updates.ContactField[fieldKey] = newField.uuid;

        for (const optionKey of Object.keys(
          importedJSON.Bag.External.ContactFieldOption || {}
        )) {
          const fieldMapping = oldField.options?.find(
            (f: any) => f.uuid === optionKey
          );
          if (fieldMapping) {
            const newFieldOption = newField.options.find(
              (f: any) => f.label === fieldMapping.label
            );
            if (newFieldOption) {
              updates.ContactFieldOption[optionKey] = newFieldOption.uuid;
            }
          }
        }
      }
    }
  });

  await Promise.all(contactFieldPromises);
};

/**
 * Create contact sets that need to be created
 */
export const createContactSets = async (
  importedJSON: any,
  manualFields: string[],
  createContactSet: (data: any) => Promise<any>,
  updates: Record<string, Record<string, string>>
): Promise<void> => {
  const contactSetPromises = Object.entries(
    importedJSON.Bag.External.ContactSet || {}
  ).map(async ([fieldKey, fieldValue]: [string, any]) => {
    // Create field if: has NEW: prefix (manual with name) OR empty and not manual (auto-create)
    const shouldCreate =
      (fieldValue && fieldValue.startsWith("NEW:")) ||
      (!fieldValue && !manualFields.includes(fieldKey));

    if (shouldCreate) {
      const oldField = importedJSON.Item.ContactGroups.find(
        (f: any) => f.uuid === fieldKey
      );
      if (!oldField) {
        console.error("Could not find old field definition for", fieldKey);
        return;
      }
      const newField = {
        ...oldField,
      };
      delete newField.order;
      newField.uuid = shortUUID();
      // If the value starts with "NEW:", use that label instead
      if (fieldValue && fieldValue.startsWith("NEW:")) {
        newField.label = fieldValue.substring(4); // Remove "NEW:" prefix
      }
      const res: any = await createContactSet(newField);
      const createdUuid = getCreatedUuid(res) || newField.uuid;
      if (createdUuid) {
        updates.ContactSet[fieldKey] = newField.uuid;
      }
    }
  });

  await Promise.all(contactSetPromises);
};

/**
 * Create reporting templates that need to be created
 */
export const createReportingTemplates = async (
  importedJSON: any,
  manualFields: string[],
  createReportingTemplate: (data: any) => Promise<any>,
  updates: Record<string, Record<string, string>>
): Promise<void> => {
  const reportingTemplatePromises = Object.entries(
    importedJSON.Bag.External.ReportingTemplate || {}
  ).map(async ([fieldKey, fieldValue]: [string, any]) => {
    // Create field if: has NEW: prefix (manual with name) OR empty and not manual (auto-create)
    const shouldCreate =
      (fieldValue && fieldValue.startsWith("NEW:")) ||
      (!fieldValue && !manualFields.includes(fieldKey));

    if (shouldCreate) {
      const oldTemplate = importedJSON.Item.ReportingTemplates.find(
        (f: any) => f.uuid === fieldKey
      );
      if (!oldTemplate) {
        console.error("Could not find old template definition for", fieldKey);
        return;
      }
      const newTemplate = {
        ...oldTemplate,
        label: generateImportedLabel(oldTemplate.label, fieldValue),
        fields:
          oldTemplate.fields?.map((field: any) => ({
            ...field,
          })) || [],
      };
      delete newTemplate.uuid;
      for (const field of newTemplate.fields) {
        delete field.uuid;
      }
      const res: any = await createReportingTemplate(newTemplate);
      const createdUuid = getCreatedUuid(res);
      if (createdUuid) {
        updates.ReportingTemplate[fieldKey] = createdUuid;
        Object.entries(
          getReportingTemplateFieldMappingUpdates(
            oldTemplate,
            res.data,
            importedJSON.Bag.External.ReportingTemplateField || {}
          )
        ).forEach(([templateFieldKey, templateFieldValue]) => {
          updates.ReportingTemplateField[templateFieldKey] = templateFieldValue;
        });
      }
    }
  });

  await Promise.all(reportingTemplatePromises);
};

/**
 * Create reporting topics that need to be created
 */
export const createReportingTopics = async (
  importedJSON: any,
  manualFields: string[],
  createReportingTopic: (data: any) => Promise<any>,
  updates: Record<string, Record<string, string>>
): Promise<void> => {
  const topicPromises = Object.entries(
    importedJSON.Bag.External.Topic || {}
  ).map(async ([fieldKey, fieldValue]: [string, any]) => {
    // Create field if: has NEW: prefix (manual with name) OR empty and not manual (auto-create)
    const shouldCreate =
      (fieldValue && fieldValue.startsWith("NEW:")) ||
      (!fieldValue && !manualFields.includes(fieldKey));

    if (shouldCreate) {
      const oldTopic = importedJSON.Item.ReportingTopics.find(
        (f: any) => f.uuid === fieldKey
      );
      if (!oldTopic) {
        console.error("Could not find old topic definition for", fieldKey);
        return;
      }
      const newTopic = {
        ...oldTopic,
        label: generateImportedLabel(oldTopic.label, fieldValue),
      };
      delete newTopic.uuid;
      const res: any = await createReportingTopic(newTopic);
      const createdUuid = getCreatedUuid(res);
      if (createdUuid) {
        updates.Topic[fieldKey] = createdUuid;
      }
    }
  });

  await Promise.all(topicPromises);
};

/**
 * Create API connections that need to be created
 */
export const createApiConnections = async (
  importedJSON: any,
  manualFields: string[],
  updateApiConnection: (data: any) => Promise<any>,
  defaultBaseUrl: string,
  updates: Record<string, Record<string, string>>
): Promise<void> => {
  const apiConnectionPromises = Object.entries(
    importedJSON.Bag.External.ApiConnection || {}
  ).map(async ([fieldKey, fieldValue]: [string, any]) => {
    // Create field if: has NEW: prefix (manual with name) OR empty and not manual (auto-create)
    const shouldCreate =
      (fieldValue && fieldValue.startsWith("NEW:")) ||
      (!fieldValue && !manualFields.includes(fieldKey));

    if (shouldCreate) {
      const res: any = await updateApiConnection({
        Status: "production",
        Kind: "third-party",
        Label:
          fieldValue && fieldValue.startsWith("NEW:")
            ? fieldValue.substring(4) // Use custom label if provided
            : fieldKey + " API Connection",
        Profile: { BaseUrl: defaultBaseUrl } as any, // minimal profile
      });
      const createdUuid = getCreatedUuid(res);
      if (createdUuid) {
        updates.ApiConnection[fieldKey] = createdUuid;
      }
    }
  });

  await Promise.all(apiConnectionPromises);
};

const getDataStructureFields = (importedJSON: any) =>
  Object.values(importedJSON.Bag.External.DataStructureField || {})
    .map((fieldValue: any) => String(fieldValue || ""))
    .filter(Boolean)
    .map((path) => ({
      Path: path,
      Name: path.split(".").pop() || path,
      DataType: "string",
      IsArray: false,
      DefaultValue: "",
      RawJson: "",
    }));

export const createDataStructures = async (
  importedJSON: any,
  manualFields: string[],
  saveApiDataCollection: (data: any) => Promise<any>,
  updates: Record<string, Record<string, string>>
): Promise<void> => {
  const fields = getDataStructureFields(importedJSON);
  const dataStructurePromises = Object.entries(
    importedJSON.Bag.External.DataStructure || {}
  ).map(async ([fieldKey, fieldValue]: [string, any]) => {
    const shouldCreate =
      (fieldValue && fieldValue.startsWith("NEW:")) ||
      (!fieldValue && !manualFields.includes(fieldKey));

    if (shouldCreate) {
      const res: any = await saveApiDataCollection({
        Uuid: "",
        Label:
          fieldValue && fieldValue.startsWith("NEW:")
            ? fieldValue.substring(4)
            : fieldKey + " Data Structure",
        Path: "/",
        Fields: fields,
        RawJson: "",
        ApiProfiles: [],
      });
      const createdUuid = getCreatedUuid(res);
      if (createdUuid) {
        updates.DataStructure[fieldKey] = createdUuid;
      }
    }
  });

  await Promise.all(dataStructurePromises);
};

/**
 * Orchestrates all field creation operations
 */
export const handleCreateFields = async (
  importedJSON: any,
  manualFields: string[],
  mutations: {
    upsertField: (data: any) => Promise<any>;
    createContactSet: (data: any) => Promise<any>;
    createReportingTemplate: (data: any) => Promise<any>;
    createReportingTopic: (data: any) => Promise<any>;
    saveApiDataCollection: (data: any) => Promise<any>;
    updateApiConnection: (data: any) => Promise<any>;
  },
  defaultBaseUrl: string,
  setDeployProgress: (progress: string) => void
): Promise<Record<string, Record<string, string>>> => {
  const updates = createEmptyFieldUpdates();

  setDeployProgress("Creating required fields...");

  // Create all field types in parallel
  await Promise.all([
    createContactFields(importedJSON, manualFields, mutations.upsertField, updates),
    createContactSets(importedJSON, manualFields, mutations.createContactSet, updates),
    createReportingTemplates(importedJSON, manualFields, mutations.createReportingTemplate, updates),
    createReportingTopics(importedJSON, manualFields, mutations.createReportingTopic, updates),
    createDataStructures(importedJSON, manualFields, mutations.saveApiDataCollection, updates),
    createApiConnections(importedJSON, manualFields, mutations.updateApiConnection, defaultBaseUrl, updates),
  ]);

  return updates;
};
